import create from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface User {
  id: number;
  profile_id?: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  gender: string;
  date_of_birth?: string;
  age?: number;
  is_photo_verified: boolean;
  is_id_verified: boolean;
  is_premium_member: boolean;
  role?: string;
  admin_permissions?: string[];
  height_cm?: number;
  religion?: string;
  caste?: string;
  sub_caste?: string;
  education?: string;
  occupation?: string;
  annual_income?: number;
  city?: string;
  state?: string;
  marital_status?: string;
  about_me?: string;
  hobbies?: string;
  languages?: string;
  dosham_status?: string;
}

interface AuthStore {
  authResolved: boolean;
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  userRole: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  authenticateWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => void;
  recordActivity: () => void;
  isSessionIdle: () => boolean;
  updateUser: (user: User) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      authResolved: false,
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      userRole: null,

      login: async (email: string, password: string) => {
        try {
          const response = await api.post('/auth/login', { email, password });
          const { access_token, refresh_token } = response.data;
          await get().authenticateWithTokens(access_token, refresh_token);
        } catch (error) {
          throw error;
        }
      },

      register: async (data: any) => {
        try {
          const response = await api.post('/auth/register', data);
          const { access_token, refresh_token } = response.data;
          await get().authenticateWithTokens(access_token, refresh_token);
        } catch (error) {
          throw error;
        }
      },

      authenticateWithTokens: async (accessToken: string, refreshToken: string) => {
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        const userResponse = await api.get('/auth/me');
        const user = userResponse.data;
        set({
          authResolved: true,
          isAuthenticated: true,
          accessToken,
          refreshToken,
          user,
          userRole: user.role || 'USER',
        });
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('sessionLastActivity', String(Date.now()));
      },

      logout: () => {
        if (localStorage.getItem('accessToken')) {
          void api.post('/auth/logout').catch(() => undefined);
        }
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sessionLastActivity');
        set({
          authResolved: true,
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          userRole: null,
        });
        delete api.defaults.headers.common['Authorization'];
      },

      checkAuth: async () => {
        const token = localStorage.getItem('accessToken');
        const lastActivity = Number(localStorage.getItem('sessionLastActivity') || 0);
        const idleTimeoutMs = 15 * 60 * 1000;
        if (token && (!lastActivity || Date.now() - lastActivity >= idleTimeoutMs)) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('sessionLastActivity');
          delete api.defaults.headers.common['Authorization'];
          set({ authResolved: true, isAuthenticated: false, user: null, accessToken: null, refreshToken: null, userRole: null });
          return;
        }
        if (!token) {
          delete api.defaults.headers.common['Authorization'];
          set({
            authResolved: true,
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            userRole: null,
          });
          return;
        }

        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          try {
            // Fetch user data with the token
            const userResponse = await api.get('/auth/me');
            const user = userResponse.data;
            set({
              authResolved: true,
              isAuthenticated: true,
              accessToken: token,
              refreshToken: localStorage.getItem('refreshToken'),
              user,
              userRole: user.role || 'USER',
            });
          } catch (error) {
            // Token is invalid, clear it
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('sessionLastActivity');
            delete api.defaults.headers.common['Authorization'];
            set({
              authResolved: true,
              isAuthenticated: false,
              accessToken: null,
              refreshToken: null,
              user: null,
              userRole: null,
            });
          }
        }
      },

      updateUser: (user: User) => {
        set({
          authResolved: true,
          user,
          isAuthenticated: true,
          accessToken: localStorage.getItem('accessToken'),
          refreshToken: localStorage.getItem('refreshToken'),
          userRole: user.role || 'USER',
        });
      },
      recordActivity: () => {
        if (localStorage.getItem('accessToken')) localStorage.setItem('sessionLastActivity', String(Date.now()));
      },
      isSessionIdle: () => {
        const lastActivity = Number(localStorage.getItem('sessionLastActivity') || 0);
        return !lastActivity || Date.now() - lastActivity >= 15 * 60 * 1000;
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        authResolved: state.authResolved,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        userRole: state.userRole,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
